/**
 * Utility to dynamically generate column configurations from backend field metadata
 *
 * This ensures ALL available fields from the backend schema appear in the columns dropdown,
 * not just a manually curated subset.
 */

import { ColumnConfig, TableRow } from '../config/types';
import {
  TextCell,
  CurrencyCell,
  CurrencyDecimalCell,
  DateCell,
  BooleanCell,
  NumberCell,
} from '../config/cellRenderers';
import { extractValue } from '../utils/dataFormatters';

/**
 * Field metadata structure from backend (ExtractedDocumentTerm.field_metadata_for)
 */
export interface FieldMetadata {
  type: 'string' | 'number' | 'date' | 'boolean' | 'enum';
  display_name: string;
  filter_operators: string[];
  group: string;
  format?: 'currency' | 'percentage' | 'number' | 'date';
  enum_values?: string[];
}

export type FieldMetadataMap = Record<string, FieldMetadata>;

/**
 * Map field metadata type/format to appropriate Cell renderer
 */
const getCellRenderer = (metadata: FieldMetadata): React.ComponentType<any> => {
  // Boolean fields
  if (metadata.type === 'boolean') {
    return BooleanCell;
  }

  // Date fields
  if (metadata.type === 'date' || metadata.format === 'date') {
    return DateCell;
  }

  // Number fields with specific formats
  if (metadata.type === 'number') {
    if (metadata.format === 'currency') {
      // Check if field typically has decimals (e.g., price per sqft, rent per sqft)
      const needsDecimals = metadata.display_name.toLowerCase().includes('per sf') ||
                           metadata.display_name.toLowerCase().includes('per sqft') ||
                           metadata.display_name.toLowerCase().includes('/sf');
      return needsDecimals ? CurrencyDecimalCell : CurrencyCell;
    }
    if (metadata.format === 'percentage') {
      return TextCell; // Format as "5.0%" in text
    }
    return NumberCell; // Plain number display
  }

  // String and enum fields - use TextCell
  return TextCell;
};

/**
 * Determine default column width based on field type and name
 */
const getDefaultWidth = (fieldKey: string, metadata: FieldMetadata): { width?: number; minWidth?: number } => {
  // Address fields need more space
  if (fieldKey.includes('address')) {
    return { width: 350, minWidth: 300 };
  }

  // Name fields need decent space
  if (fieldKey.includes('name')) {
    return { width: 280, minWidth: 250 };
  }

  // Date fields
  if (metadata.type === 'date' || metadata.format === 'date') {
    return { width: 200, minWidth: 180 };
  }

  // Currency fields
  if (metadata.format === 'currency') {
    return { width: 180, minWidth: 150 };
  }

  // Boolean fields can be narrow
  if (metadata.type === 'boolean') {
    return { width: 120, minWidth: 100 };
  }

  // Default width for other fields
  return { width: 180, minWidth: 150 };
};

/**
 * Convert snake_case field key to kebab-case for CSS class names
 */
const toKebabCase = (str: string): string => {
  return str.replace(/_/g, '-');
};

/**
 * Determine if a field should be visible by default
 * Only show the most important fields initially to avoid overwhelming users
 */
const isDefaultVisible = (fieldKey: string, metadata: FieldMetadata, documentType: string): boolean => {
  // Always visible base fields (handled separately, not in metadata)
  // These are title, tenant/buyer, landlord/seller, property_address, square_feet

  // For leases
  if (documentType === 'lease') {
    // Default visible: rent, term, dates
    const defaultLeaseFields = [
      'initial_monthly_rent',
      'lease_term_months',
      'commencement_date',
      'termination_date',
    ];
    return defaultLeaseFields.includes(fieldKey);
  }

  // For PSAs
  if (documentType === 'psa') {
    // Default visible: price, deposit, dates
    const defaultPSAFields = [
      'total_purchase_price',
      'price_per_sqft',
      'closing_date',
      'due_diligence_period_days',
    ];
    return defaultPSAFields.includes(fieldKey);
  }

  return false;
};

/**
 * Generate column configuration from a single field metadata entry
 */
export const fieldMetadataToColumn = (
  fieldKey: string,
  metadata: FieldMetadata,
  documentType: string
): ColumnConfig => {
  const { width, minWidth } = getDefaultWidth(fieldKey, metadata);

  return {
    id: fieldKey,
    header: metadata.display_name,
    accessor: (row: TableRow) =>
      extractValue(row.originalDocument?.extractedDocumentTerm?.termsData, fieldKey),
    defaultVisible: isDefaultVisible(fieldKey, metadata, documentType),
    width,
    minWidth,
    className: `${toKebabCase(fieldKey)}-column`,
    documentTypes: [documentType as 'lease' | 'psa'],
    Cell: getCellRenderer(metadata),
  };
};

/**
 * Generate all column configurations from field metadata map
 *
 * @param fieldMetadata - Map of field keys to metadata from backend
 * @param documentType - 'lease' or 'psa'
 * @param baseColumns - Required base columns (e.g., document name) that aren't in metadata
 * @returns Complete array of column configurations
 */
export const generateColumnsFromMetadata = (
  fieldMetadata: FieldMetadataMap,
  documentType: string,
  baseColumns: ColumnConfig[]
): ColumnConfig[] => {
  // Convert all metadata fields to column configs
  const metadataColumns = Object.entries(fieldMetadata).map(([fieldKey, metadata]) =>
    fieldMetadataToColumn(fieldKey, metadata, documentType)
  );

  // Combine base columns (like document name) with metadata-generated columns
  return [...baseColumns, ...metadataColumns];
};

/**
 * Sort columns for display in dropdown:
 * 1. Active/visible columns first (alphabetical by header)
 * 2. Then hidden columns (alphabetical by header)
 *
 * @param columns - All column configurations
 * @param visibleColumnIds - Set of currently visible column IDs
 * @returns Sorted column configurations
 */
export const sortColumnsForDropdown = (
  columns: ColumnConfig[],
  visibleColumnIds: Set<string>
): ColumnConfig[] => {
  // Split into visible and hidden
  const visibleColumns = columns.filter(col => visibleColumnIds.has(col.id));
  const hiddenColumns = columns.filter(col => !visibleColumnIds.has(col.id));

  // Sort each group alphabetically by header
  const sortByHeader = (a: ColumnConfig, b: ColumnConfig) =>
    a.header.localeCompare(b.header);

  visibleColumns.sort(sortByHeader);
  hiddenColumns.sort(sortByHeader);

  // Return visible first, then hidden
  return [...visibleColumns, ...hiddenColumns];
};
